import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { omit } from 'ramda';
import { useBoolean } from 'shared/web';
import { PresetApiStateEnum } from '../constants/PresetApiStateEnum';
type UsePresetApiStateProps = {
  updatePreset: (presetId: string) => Promise<void> | void;
  duplicatePreset: (presetId: string) => Promise<void>;
  deletePreset: (presetId: string) => Promise<void>;
  updatePresetPosition: (from: number, to: number) => Promise<void>;
};
type UsePresetApiStateResult = {
  apiState: Record<string, PresetApiStateEnum>;
  isPresetChangeDisabled: boolean;
  disabledPresetChange: () => void;
  allowPresetChange: () => void;
  updatePresetProxy: (presetId: string) => void;
  duplicatePresetProxy: (presetId: string) => void;
  deletePresetProxy: (presetId: string) => void;
  updatePresetPositionProxy: (presetId: string, from: number, to: number) => void;
};

/**
 * Manages per-preset API loading/success/error state for CRUD operations.
 *
 * Extracted from PresetPanel to keep the component lean and allow the state
 * machine to be tested independently.
 */
export function usePresetApiState(props) {
  const $ = _c(24);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [apiState, setApiState] = useState(t0);
  const [isPresetChangeDisabled, disabledPresetChange, allowPresetChange] = useBoolean();
  let t1;
  if ($[1] !== allowPresetChange || $[2] !== disabledPresetChange || $[3] !== props) {
    t1 = presetId => {
      disabledPresetChange();
      setApiState(prev => ({
        ...prev,
        [presetId]: PresetApiStateEnum.loading
      }));
      const updateResult = props.updatePreset(presetId);
      const resolvedPromise = updateResult != null && typeof updateResult === "object" ? updateResult as Promise<void> : null;
      if (resolvedPromise == null) {
        return setTimeout(() => setApiState(prev_0 => ({
          ...prev_0,
          [presetId]: PresetApiStateEnum.idle
        })), 1200);
      }
      resolvedPromise.then(() => setApiState(prev_1 => ({
        ...prev_1,
        [presetId]: PresetApiStateEnum.success
      }))).catch(() => setApiState(prev_2 => ({
        ...prev_2,
        [presetId]: PresetApiStateEnum.error
      }))).finally(() => {
        setTimeout(() => setApiState(prev_3 => ({
          ...prev_3,
          [presetId]: PresetApiStateEnum.idle
        })), 1200);
        allowPresetChange();
      });
    };
    $[1] = allowPresetChange;
    $[2] = disabledPresetChange;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const updatePresetProxy = t1;
  let t2;
  if ($[5] !== allowPresetChange || $[6] !== disabledPresetChange || $[7] !== props) {
    t2 = presetId_0 => {
      disabledPresetChange();
      setApiState(prev_4 => ({
        ...prev_4,
        [presetId_0]: PresetApiStateEnum.loading
      }));
      props.duplicatePreset(presetId_0).then(() => setApiState(prev_5 => ({
        ...prev_5,
        [presetId_0]: PresetApiStateEnum.success
      }))).catch(() => setApiState(prev_6 => ({
        ...prev_6,
        [presetId_0]: PresetApiStateEnum.error
      }))).finally(() => {
        setTimeout(() => setApiState(prev_7 => ({
          ...prev_7,
          [presetId_0]: PresetApiStateEnum.idle
        })), 1200);
        allowPresetChange();
      });
    };
    $[5] = allowPresetChange;
    $[6] = disabledPresetChange;
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const duplicatePresetProxy = t2;
  let t3;
  if ($[9] !== props) {
    t3 = presetId_1 => {
      setApiState(prev_8 => ({
        ...prev_8,
        [presetId_1]: PresetApiStateEnum.loading
      }));
      props.deletePreset(presetId_1).then(() => setApiState(prev_9 => ({
        ...prev_9,
        [presetId_1]: PresetApiStateEnum.success
      }))).catch(() => setApiState(prev_10 => ({
        ...prev_10,
        [presetId_1]: PresetApiStateEnum.error
      }))).finally(() => setTimeout(() => setApiState(prev_11 => omit([presetId_1], prev_11)), 1200));
    };
    $[9] = props;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const deletePresetProxy = t3;
  let t4;
  if ($[11] !== allowPresetChange || $[12] !== disabledPresetChange || $[13] !== props) {
    t4 = (presetId_2, from, to) => {
      disabledPresetChange();
      setApiState(prev_12 => ({
        ...prev_12,
        [presetId_2]: PresetApiStateEnum.loading
      }));
      props.updatePresetPosition(from, to).then(() => setApiState(prev_13 => ({
        ...prev_13,
        [presetId_2]: PresetApiStateEnum.success
      }))).catch(() => setApiState(prev_14 => ({
        ...prev_14,
        [presetId_2]: PresetApiStateEnum.error
      }))).finally(() => {
        setTimeout(() => setApiState(prev_15 => ({
          ...prev_15,
          [presetId_2]: PresetApiStateEnum.idle
        })), 1200);
        allowPresetChange();
      });
    };
    $[11] = allowPresetChange;
    $[12] = disabledPresetChange;
    $[13] = props;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const updatePresetPositionProxy = t4;
  let t5;
  if ($[15] !== allowPresetChange || $[16] !== apiState || $[17] !== deletePresetProxy || $[18] !== disabledPresetChange || $[19] !== duplicatePresetProxy || $[20] !== isPresetChangeDisabled || $[21] !== updatePresetPositionProxy || $[22] !== updatePresetProxy) {
    t5 = {
      apiState,
      isPresetChangeDisabled,
      disabledPresetChange,
      allowPresetChange,
      updatePresetProxy,
      duplicatePresetProxy,
      deletePresetProxy,
      updatePresetPositionProxy
    };
    $[15] = allowPresetChange;
    $[16] = apiState;
    $[17] = deletePresetProxy;
    $[18] = disabledPresetChange;
    $[19] = duplicatePresetProxy;
    $[20] = isPresetChangeDisabled;
    $[21] = updatePresetPositionProxy;
    $[22] = updatePresetProxy;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
}